import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { JsonDataContainerFragment, VipFragment } from '@graphql/generated';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useSaveJsonData } from '@website-editor/customComponents/CustomJsonComponent/hooks/useJsonBlobStorageUtils';
import { VipData } from '../guest/VipGuest';

function getOrderedIds(sortOrder: string[], excludedIDs: string[]) {
  const excludedSet = new Set(excludedIDs);

  // Only include IDs not excluded, and preserve order from sortOrder if present
  const includedIds = (sortOrder ?? []).filter(id => !excludedSet.has(id));

  // Final sort order: only non-excluded IDs, in order
  return [...includedIds];
}

export const usePresentationDataState = (componentData: JsonDataContainerFragment, vips: VipFragment[]) => {
  const [isFiltering, setIsFiltering] = useState(false);
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { saveJsonData } = useSaveJsonData();

  const initialPresentationData = useMemo(() => {
    const content = componentData.content as VipData;
    const excludedIDs = content.exclusiveFilterIDs ?? [];
    return {
      sortOrder: getOrderedIds(content.sortOrder ?? [], excludedIDs),
      excludedIDs,
    };
  }, [componentData.content]);

  const lastState = useRef(initialPresentationData);
  const [presentationData, setPresentationData] = useState(initialPresentationData);

  const savePresentationData = async () => {
    await saveJsonData({
      id: componentData.id,
      content: {
        ...componentData.content,
        sortOrder: presentationData.sortOrder,
        exclusiveFilterIDs: presentationData.excludedIDs,
      },
    });

    updateWebsitePreview();
    lastState.current = presentationData;
  };

  const handleFilterClick = (id: string, isExcluding: boolean) => {
    setPresentationData(prev => {
      const newExcludedIDs = isExcluding ? [...prev.excludedIDs, id] : prev.excludedIDs.filter(eid => eid !== id);
      // If we're including, add to the end of the sort order
      const newlyIncluded = !isExcluding ? [id] : [];

      return {
        ...prev,
        excludedIDs: newExcludedIDs,
        sortOrder: [...getOrderedIds(prev.sortOrder, newExcludedIDs), ...newlyIncluded],
      };
    });
  };

  const handleReorder = (id: string, direction: 'up' | 'down') => {
    setPresentationData(prev => {
      const excludedSet = new Set(prev.excludedIDs);

      // Only reorder non-excluded items
      const includedIds = prev.sortOrder.filter(id => !excludedSet.has(id));

      const idx = includedIds.indexOf(id);
      if (idx === -1) return prev;

      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= includedIds.length) return prev;

      const newIncludedIds = [...includedIds];
      [newIncludedIds[idx], newIncludedIds[swapWith]] = [newIncludedIds[swapWith], newIncludedIds[idx]];

      return {
        ...prev,
        sortOrder: newIncludedIds,
      };
    });
  };

  const revertPresentationData = () => {
    setPresentationData(lastState.current);
  };

  // Keep sortOrder in sync with non-excluded VIPs
  useEffect(() => {
    // Only include non-excluded VIPs
    const nonExcludedVipIds = vips.filter(vip => !presentationData.excludedIDs.includes(vip.id)).map(vip => vip.id);
    // Remove IDs from sortOrder that are no longer in nonExcludedVipIds
    let nextSortOrder = presentationData.sortOrder.filter(id => nonExcludedVipIds.includes(id));
    const nextSortOrderSet = new Set(nextSortOrder);
    const missingIds = nonExcludedVipIds.filter(id => !nextSortOrderSet.has(id));
    if (missingIds.length > 0 || nextSortOrder.length !== presentationData.sortOrder.length) {
      nextSortOrder = [...nextSortOrder, ...missingIds];
      setPresentationData(prev => ({
        ...prev,
        sortOrder: nextSortOrder,
      }));
    }
  }, [vips, presentationData.sortOrder, presentationData.excludedIDs]);

  return {
    isFiltering,
    setIsFiltering,
    presentationData,
    setPresentationData,
    revertPresentationData,
    handleFilterClick,
    handleReorder,
    savePresentationData,
  };
};
